// features/patient/pages/GeneralTab.tsx

import { Box, Card, CardContent, IconButton, Tooltip, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { format } from 'date-fns';
import React from 'react';
import { patientData } from './PatientData';

import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ArrowUpwardIcon from '@mui/icons-material/NorthEast';
import ArrowDownwardIcon from '@mui/icons-material/SouthEast';

import { Area, AreaChart, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, XAxis, YAxis } from 'recharts';

enum DifferenceEnum {
  up = 'up',
  down = 'down',
  neutral = 'neutral',
}

function formatMinutesToHours(minutes: number): string {
  const hours = Math.floor(minutes / 60); // Calculate the number of hours
  const remainingMinutes = Math.trunc(minutes % 60); // Calculate the remaining minutes
  return `${hours.toString()} h ${remainingMinutes.toString().padStart(2, '0')}`; // Format as "x h yy"
}

function formatDifference(now: number, past: number): DifferenceEnum {
  if ((now - past) / now > 0.03) {
    return DifferenceEnum.up;
  } else if ((now - past) / now < -0.03) {
    return DifferenceEnum.down;
  } else {
    return DifferenceEnum.neutral;
  }
}

// Helper component for arrow + difference label
interface DifferenceProps {
  differenceText: string;
  differenceType: 'up' | 'down' | 'neutral';
  // e.g. differenceText="+3 pts", differenceType="up"
}
const DifferenceIndicator: React.FC<DifferenceProps> = ({ differenceText, differenceType }) => {
  let IconComponent: React.ReactNode = <ArrowForwardIcon fontSize="small" />;
  if (differenceType === 'up') {
    IconComponent = <ArrowUpwardIcon fontSize="small" />;
  } else if (differenceType === 'down') {
    IconComponent = <ArrowDownwardIcon fontSize="small" />;
  }

  return (
    <Box
      alignItems="center"
      display="flex"
      sx={{
        borderRadius: 5,
        bgcolor: 'background.paper',
        color: 'primary.main',
        border: 1,
        px: 1,
        py: 0.5,
        mr: 10,
        mt: 2,
        mb: 2,
      }}
    >
      {IconComponent} &nbsp;&nbsp;
      <Typography sx={{ color: 'primary.main', fontWeight: 'bold' }} variant="h6">
        {differenceText}
      </Typography>
    </Box>
  );
};

const formatXAxis = (tickItem: Date) => {
  return format(tickItem, 'dd MMM');
};

const formatTooltip = (value: number) => {
  return value;
};
const formatTooltipMinutes = (value: number) => {
  return formatMinutesToHours(value);
};

const formatYAxis = (value: number) => {
  return value.toString();
};

const formatYAxisMinutes = (value: number) => {
  return formatMinutesToHours(value);
};

interface MetricCardProps {
  id: string;
  title: string;
  mainValue: number;
  previousValue: number;
  unit?: string;
  subText?: string; // e.g. "Par rapport aux 4 semaines précédentes"
  chartData: { day: Date; value: number }[];
  chartColor?: string;
  timeValue?: boolean; // the value is in min and we want to display it in hh:mm
}

const MetricCard: React.FC<MetricCardProps> = ({
  id,
  title,
  mainValue,
  previousValue,
  subText,
  unit = '',
  chartData,
  chartColor = '#6B6BFC',
  timeValue = false, // the value is in min and we want to display it in hh:mm
}) => {
  return (
    <Card
      sx={{
        borderRadius: 2,
        boxShadow: 0,
        backgroundColor: 'primary.light',
      }}
    >
      <CardContent>
        {/* Title + "?" icon */}
        <Grid container>
          <Grid size={12}>
            <Typography fontWeight="bold" sx={{ color: 'primary.main', mb: 1 }} variant="h5">
              {title}
              <Tooltip title="Plus d’informations">
                <IconButton size="small" sx={{ color: 'primary.main' }}>
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Typography>
          </Grid>
          <Grid size={4}>
            {/* Main Value */}
            <Typography fontWeight="bold" sx={{ mt: 1, mb: 0.5, color: 'primary.main' }} variant="h4">
              {timeValue ? formatMinutesToHours(mainValue) : Math.trunc(mainValue)}
            </Typography>

            {/* Difference indicator */}
            <DifferenceIndicator
              differenceText={`${Math.trunc(mainValue - previousValue).toString()} ${unit}`}
              differenceType={formatDifference(mainValue, previousValue)}
            />

            {/* Subtext */}
            {subText && (
              <Typography color="text.secondary" sx={{ display: 'block', mt: 3, color: 'primary.main' }} variant="caption">
                {subText}
              </Typography>
            )}
          </Grid>
          <Grid
            size={8}
            sx={{
              bgcolor: 'background.paper',
              p: 1,
              minHeight: 200,
              borderRadius: 2,
            }}
          >
            {/* Tiny Chart */}
            <ResponsiveContainer>
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id={`${id}-color`} x1="0" x2="0" y1="0" y2="1">
                    <stop offset="10%" stopColor={chartColor} stopOpacity={0.7} />
                    <stop offset="85%" stopColor={chartColor} stopOpacity={0.1} />
                  </linearGradient>
                </defs>
                <XAxis angle={-45} dataKey="day" tick={{ textAnchor: 'end', fontSize: 10 }} tickFormatter={formatXAxis} />
                <YAxis tick={{ textAnchor: 'end', fontSize: 10 }} tickFormatter={timeValue ? formatYAxisMinutes : formatYAxis} />
                <RechartsTooltip cursor={false} formatter={timeValue ? formatTooltipMinutes : formatTooltip} labelFormatter={formatXAxis} />
                <CartesianGrid strokeDasharray="3 0" vertical={false} />
                <Area dataKey="value" fill={`url(#${id}-color)`} fillOpacity={1} stroke={chartColor} strokeWidth={2} type="monotone" />
              </AreaChart>
            </ResponsiveContainer>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

const DashboardGeneral: React.FC = () => {
  const sleepData = patientData.sleep.period.map((d) => ({ day: d.date, value: d.value }));
  const stepsData = patientData.steps.period.map((d) => ({ day: d.date, value: d.value }));
  const wakesData = patientData.total_wakes_episodes.period.map((d) => ({ day: d.date, value: d.value }));
  const phq9Data = patientData.phq9.period.map((d) => ({ day: d.date, value: d.value }));
  const phq9Data_period_average = patientData.phq9.period_average;

  return (
    <Box>
      {/* Page Title */}
      <Typography fontWeight="bold" sx={{ mb: 3, color: 'primary.main' }} variant="h5">
        Résumé des 4 dernières semaines
      </Typography>

      <Grid container spacing={4}>
        {/* Durée de sommeil moyenne */}
        <Grid size={6}>
          <MetricCard
            chartData={sleepData}
            id="sleep"
            mainValue={patientData.sleep.period_average}
            previousValue={patientData.sleep.previous_period_average}
            subText="Par rapport aux 4 semaines précédentes"
            timeValue={true}
            title="Durée de sommeil moyenne"
            unit="min"
          />
        </Grid>

        {/* Nombre de pas moyen */}
        <Grid size={6}>
          <MetricCard
            chartData={stepsData}
            id="steps"
            mainValue={patientData.steps.period_average}
            previousValue={patientData.steps.previous_period_average}
            subText="Par rapport aux 4 semaines précédentes"
            title="Nombre de pas moyen"
          />
        </Grid>

        {/* Dernier score PHQ-9 */}
        <Grid size={6}>
          <MetricCard
            chartData={phq9Data}
            id="phq9"
            mainValue={phq9Data_period_average}
            previousValue={phq9Data_period_average + 3} // dummy value
            subText="Par rapport au précédent score"
            title="Dernier score PHQ-9"
          />
        </Grid>

        {/* Nombre de réveils nocturnes moyen */}
        <Grid size={6}>
          <MetricCard
            chartData={wakesData}
            id="wakes"
            mainValue={patientData.total_wakes_episodes.period_average}
            previousValue={patientData.total_wakes_episodes.previous_period_average}
            subText="Par rapport aux 4 semaines précédentes"
            title="Nombre de réveils nocturnes moyen"
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default DashboardGeneral;
